import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setFilter } from 'app/slices/dashboardSlice';
import { toFilterParam } from '../dashboardUtils';
import type { IDashboardColumn } from 'models';

/**
 * Column header row (§3.E "Column header row"). Renders:
 *  - the FIXED Project Name cell (static frozen first column) with its sort
 *    control + name filter input, and
 *  - the dynamic columns (`#list_heading_noname`) for every visible column in
 *    order, each with a per-column sort control + search input.
 *
 * SORT: clicking a column's sort label opens the SortConfirm dialog (the parent
 * provides `onRequestSort(field)`); the `.selected` label + up/down caret track
 * the current sort (sortBy/sortOrder).
 *
 * SEARCH: typing debounces 500ms → dashboardSlice.filters → refetch
 * (server-side icontains). className is exactly "filter" (the §10 item-14 class
 * pollution bug is intentionally dropped). The value reflects the persisted
 * per-column filter so it survives re-render (parity 49a-49e).
 */
interface ColumnHeaderRowProps {
  columns: IDashboardColumn[]; // ordered, visible (dynamic) columns
  onRequestSort: (field: string) => void;
}

const DEBOUNCE_MS = 500;

/** A single per-column search input (debounced commit to the filters slice). */
const FilterInput = ({
  field,
  index,
  withDataId,
}: {
  field: string;
  index?: number;
  withDataId: boolean;
}) => {
  const dispatch = useAppDispatch();
  // The filters map is keyed by the QUERY PARAM, not the column's field_name:
  // OLD stripped the `_id` suffix before building the query hash
  // (header.js convertToNormalName). The input's `name` attribute below stays
  // on the raw field_name, matching OLD's `name="{{field_name}}"`.
  const param = toFilterParam(field);
  const persisted = useAppSelector((s) => s.dashboard.filters[param] ?? '');
  // Controlled local value for smooth typing; the slice commit is debounced so a
  // refetch only fires after the user pauses (parity 159-160). Persisted value
  // survives re-render (parity 49c/49e).
  const [local, setLocal] = useState(persisted);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local in sync if the persisted filter changes externally (e.g. status
  // sync / clear) and is not mid-edit.
  useEffect(() => {
    setLocal(persisted);
  }, [persisted]);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocal(value);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        dispatch(setFilter({ field: param, value }));
      }, DEBOUNCE_MS);
    },
    [dispatch, param]
  );

  // Ignore Tab keydown (keyCode 9) — does not trigger a filter (parity 173).
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' && timer.current) clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  return (
    <label>
      <input
        type="search"
        name={field}
        className="filter"
        placeholder="search"
        value={local}
        {...(withDataId && index != null ? { 'data-id': index } : {})}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      <i className="icon-search-1" />
      <span className="placeholder-label">search</span>
    </label>
  );
};

const ColumnHeaderRow = ({ columns, onRequestSort }: ColumnHeaderRowProps) => {
  const sortBy = useAppSelector((s) => s.dashboard.sortBy);
  const sortOrder = useAppSelector((s) => s.dashboard.sortOrder);

  /** Sort label markup shared by the fixed name cell + dynamic columns. */
  const caretClass = (field: string) => {
    const selected = sortBy === field;
    const dir = selected ? (sortOrder === 'asc' ? 'up' : 'down') : 'down';
    return `icon-arrow-${dir}-1`;
  };

  // The fixed Project Name cell is rendered by the parent (DashboardShell) so it
  // stays inside #list_heading > li.master-list-heading.fixed-column; this
  // component renders ONLY the dynamic columns into #list_heading_noname.
  return (
    <>
      {columns.map((col, index) => {
        const selected = sortBy === col.field_name;
        // NOTE: do NOT apply `css_column_class` (xlong/xxlong/medium/…) to the
        // header <li>. The live whiteboard sizes every column via the
        // `.admin-table .list-heading > li:nth-of-type(2) ul li[name=…]` (and the
        // parallel `.list-rows … li[name=…]`) attribute selectors plus a 220px
        // default — applied IDENTICALLY to header and body so they stay aligned.
        // The `.first-fixed-table-content li.xlong{width:350px!important}` /
        // `li.xxlong{width:299px!important}` rules are a DIFFERENT context; OLD
        // never puts those classes on dynamic header cells. Adding them here made
        // the header cells wider than the body (e.g. project_owner 350 vs 220),
        // pushing later headers off-screen. Render the bare `li[name]` like OLD.
        return (
          <li key={col.id} name={col.field_name}>
            <div className="project-header" {...{ for: `#${col.field_name}` }}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                className={`sorting-btn label-${col.field_name} for-project${
                  selected ? ' selected' : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => onRequestSort(col.field_name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onRequestSort(col.field_name);
                }}
              >
                <h4>
                  {col.name}
                  <i className={caretClass(col.field_name)} />
                </h4>
              </label>
            </div>
            <FilterInput field={col.field_name} index={index} withDataId />
          </li>
        );
      })}
    </>
  );
};

export { FilterInput };
export default ColumnHeaderRow;
